import React, { useState, useMemo } from 'react'
import { Modal, Input, Button, Tag, Tooltip, Icon, Popover, Spin } from 'antd'
import { ImagePreview } from '../../../../../../components'
import './CharacterManagerModal.scss'

const CharacterManagerModal = ({
  visible,
  onClose,
  characters = [],
  onAddCharacter,
  isFetchingCharacters
}) => {
  const [searchText, setSearchText] = useState('')

  // --- Filtering ---
  const filteredCharacters = useMemo(() => {
    const q = searchText.toLowerCase()
    if (!q) return characters

    return characters.filter(c => {
      const attrs = c.attributes || {}
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.gender || '').toLowerCase().includes(q) ||
        (attrs.original_prompt || '').toLowerCase().includes(q) ||
        (attrs.character_apparel || '').toLowerCase().includes(q)
      )
    })
  }, [characters, searchText])

  // --- Render Card ---
  const renderCharacterCard = char => {
    const attrs = char.attributes || {}
    const pic = attrs.profile_pic || ''
    const prompt = attrs.original_prompt || ''

    // Helper to safely parse the apparel string/array
    let apparelList = []
    try {
      if (Array.isArray(attrs.character_apparel)) {
        apparelList = attrs.character_apparel
      } else if (typeof attrs.character_apparel === 'string') {
        // Handle stringified array like "['item1', 'item2']"
        let cleanStr = attrs.character_apparel.trim()

        // Try standard JSON parse first (replace single quotes if needed but careful with internal single quotes)
        // A safer simple parse for this specific format "['a', 'b']":
        if (cleanStr.startsWith('[') && cleanStr.endsWith(']')) {
          // Remove brackets
          cleanStr = cleanStr.substring(1, cleanStr.length - 1)
          // Split by comma
          apparelList = cleanStr
            .split(',')
            .map(item => {
              return item.trim().replace(/^['"]|['"]$/g, '') // Remove surrounding quotes
            })
            .filter(Boolean)
        } else {
          // Just a comma separated string
          apparelList = cleanStr
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        }
      }
    } catch (e) {
      console.warn('Failed to parse apparel:', attrs.character_apparel)
      apparelList = []
    }

    let genderColor = 'other' // default
    if (char.gender && char.gender.toLowerCase() === 'male')
      genderColor = 'male'
    if (char.gender && char.gender.toLowerCase() === 'female')
      genderColor = 'female'

    return (
      <div key={char.id || char.name} className="character-card-wrapper">
        <div className="card-image-box">
          <ImagePreview
            src={pic}
            alt={char.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="card-info">
          <div className="card-header-row">
            <Tooltip title={char.name}>
              <div className="card-name">{char.name}</div>
            </Tooltip>
            {char.gender && (
              <span className={`gender-tag ${genderColor}`}>
                {char.gender.slice(0, 1)}
              </span>
            )}
          </div>

          <Tooltip title={prompt} placement="bottomLeft">
            <div className="card-prompt">
              {prompt || 'No description available.'}
            </div>
          </Tooltip>

          {apparelList && apparelList.length > 0 && (
            <div className="card-apparel">
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  marginTop: '4px'
                }}
              >
                {apparelList.slice(0, 3).map((item, idx) => (
                  <Tag
                    key={idx}
                    style={{ margin: 0, fontSize: '10px', background: '#fff' }}
                  >
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </Tag>
                ))}
                {apparelList.length > 3 && (
                  <Popover
                    content={
                      <div
                        style={{
                          maxWidth: '200px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '4px'
                        }}
                      >
                        {apparelList.slice(3).map((item, idx) => (
                          <Tag
                            key={idx}
                            style={{ margin: 0, fontSize: '10px' }}
                          >
                            {item.charAt(0).toUpperCase() + item.slice(1)}
                          </Tag>
                        ))}
                      </div>
                    }
                    title="More Apparel"
                    trigger="hover"
                  >
                    <Tag
                      style={{ margin: 0, fontSize: '10px', cursor: 'pointer' }}
                    >
                      +{apparelList.length - 3}
                    </Tag>
                  </Popover>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" type="default" onClick={onClose} size="large">
          Close
        </Button>
      ]}
      width={1000}
      centered
      className="character-manager-modal"
      title={null}
      closable={false}
      maskClosable={true}
    >
      {/* Header Area */}
      <div className="ant-modal-header">
        <div className="modal-title">
          <div className="modal-icon">
            <Icon type="team" />
          </div>
          Manage Characters
        </div>
      </div>

      {/* Sticky Topbar */}
      <div className="manage-modal-topbar">
        <Input
          className="custom-search-bar"
          size="large"
          placeholder="Search by name, prompt or apparel..."
          suffix={<Icon type="search" />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
        />
        <Button
          type="primary"
          icon="plus"
          onClick={onAddCharacter}
          className="add-character-btn"
        >
          Add Character
        </Button>
      </div>

      {/* Content */}
      <div className="manager-content-scroll">
        {isFetchingCharacters ? (
          <div className="empty-state">
            <Spin size="large" tip="Fetching characters..." />
          </div>
        ) : filteredCharacters.length > 0 ? (
          <div className="characters-grid">
            {filteredCharacters.map(renderCharacterCard)}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <span role="img" aria-label="empty">
                👻
              </span>
            </div>
            <div className="empty-title">
              {searchText ? 'No matches found' : 'No characters yet'}
            </div>
            <div className="empty-desc">
              {searchText
                ? `We couldn't find any characters matching "${searchText}". Try a different term.`
                : 'Get started by adding your first character to the library.'}
            </div>
            {!searchText && (
              <Button
                type="primary"
                style={{ marginTop: 20 }}
                onClick={onAddCharacter}
              >
                Create Character
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default CharacterManagerModal
